'use client';

import { LAYER_NAMES, type LayerName, LAYERS, RECONCILED } from '@slims/shared/energy';
import { PodLayerColors, PodLayerLabels, PodLayersChart, type PodLayerPoint } from '../../charts';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '../../molecules/dialog/dialog';
import { LabeledSelect } from '../../molecules/labeled-select/labeled-select';

/**
 * Un punto di connessione per intero: produzione **e** consumo, ciascuno
 * spezzato nei livelli di dato che possediamo.
 *
 * È la vista che l'esploso per misuratore non poteva dare. Quel dialogo prende
 * un punto di misura e mostra immessa *oppure* prelevata — quale delle due
 * dipende dal misuratore che gli si passa — mentre un POD prosumer è due punti
 * di misura sotto lo stesso codice, e la domanda che si fa guardandolo («produce
 * più di quanto consuma? e quando?») ha bisogno di vederli accanto.
 *
 * Per ciascuna direzione si vedono tre cose: la curva di ogni livello, quanto
 * ciascuno copre dell'intervallo, e la risultante che i KPI hanno usato.
 */

export type PodDetailBucket = 'slot' | 'hour' | 'day' | 'month';
export type PodDetailDirection = 'produzione' | 'consumo' | 'accumulo';

export interface PodDetailPlant {
	id: string;
	censimp: string | null;
	codiceGaudi: string | null;
	potenzaKwp: number | null;
	dataEsercizio: string | null;
}

export interface PodDetailMeter {
	id: string;
	puntoMisura: string | null;
	kind: string;
	tipo: string;
	plants: PodDetailPlant[];
}

export interface PodDetailSection {
	direction: PodDetailDirection;
	points: PodLayerPoint[];
	totals: Record<LayerName | typeof RECONCILED, number>;
	coverage: Record<LayerName, number> & { totalSlots: number };
	/**
	 * Gli slot del livello reale spezzati per provenienza.
	 *
	 * Rilevato e bolletta sono **un livello solo** — è la misura della stessa
	 * grandezza con due strumenti, e il rilevato vince — ma quanta parte del
	 * periodo sia stata ricostruita da una fattura invece che misurata è
	 * esattamente ciò che un operatore deve poter vedere prima di fidarsi del
	 * numero. Come chip separato faceva credere a una gerarchia che non c'è.
	 */
	realeByOrigin?: { rilevato: number; bolletta: number };
	meters: PodDetailMeter[];
}

/** Una voce del selettore: quanto basta per riconoscere un POD in una tendina. */
export interface PodDetailOption {
	podId: string;
	codice: string;
	memberName: string | null;
}

export interface PodDetailDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Codice POD del punto di connessione. */
	codice: string;
	memberName: string | null;
	cabinaPrimaria: string | null;
	/** Intervallo già formattato per chi legge. */
	periodo: string;
	bucket: PodDetailBucket;
	sections: PodDetailSection[];
	unit?: string;
	loading?: boolean;
	/**
	 * Gli altri POD su cui si può passare senza uscire dal dettaglio.
	 *
	 * Cambiare POD **non** cambia intervallo né grano: è ciò che rende il
	 * confronto una lettura e non un esercizio di memoria — due POD guardati su
	 * finestre diverse non si possono confrontare, e con un dialogo che si
	 * riapre ogni volta è esattamente quello che succede.
	 */
	pods?: PodDetailOption[];
	/** Il POD mostrato, per evidenziarlo nel selettore. */
	podId?: string;
	/** Passa a un altro POD; assente = selettore nascosto. */
	onSelectPod?: (podId: string) => void;
	/** Cambio di grano/finestra; assente = selettore nascosto. */
	onBucketChange?: (bucket: PodDetailBucket) => void;
	/**
	 * I grani offerti. Assente = tutti e quattro.
	 *
	 * Serve dove la finestra è fissa perché ereditata dal pannello che ha aperto
	 * il dialogo: lì un grano fuori scala non si può assorbire stringendo o
	 * allargando l'intervallo, e va tolto dalla scelta invece che corretto dopo.
	 * Con un solo grano possibile il selettore sparisce: non è una scelta.
	 */
	buckets?: PodDetailBucket[];
	/**
	 * Navigazione fra intervalli contigui. Entrambe assenti = niente frecce, ed è
	 * il caso di chi arriva da un grafico: la finestra è quella che il pannello
	 * stava mostrando, e scorrerla altrove vorrebbe dire guardare un intervallo
	 * che nel grafico di partenza non c'era.
	 */
	onPrev?: () => void;
	onNext?: () => void;
	nextDisabled?: boolean;
}

const DIRECTION_LABEL: Record<PodDetailDirection, string> = {
	produzione: 'Produzione',
	consumo: 'Consumo',
	accumulo: 'Accumulo',
};

const DIRECTION_HINT: Record<PodDetailDirection, string> = {
	produzione: 'Punto di misura in immissione (M2)',
	consumo: 'Punto di misura in prelievo (M1)',
	// Vale la pena dirlo: `rebuild_day_curve` proietta l'accumulo nel ramo
	// prelievo, quindi il numero esiste ma non è una direzione a sé nella curva.
	accumulo: 'Punto di misura di accumulo (M3) - misurato come prelievo',
};

const BUCKETS: { value: PodDetailBucket; label: string }[] = [
	{ value: 'slot', label: 'Giorno' },
	{ value: 'hour', label: 'Ore' },
	{ value: 'day', label: 'Giorni' },
	{ value: 'month', label: 'Mesi' },
];

const LAYER_KEYS = LAYER_NAMES;

function fmtKwh(value: number, unit: string): string {
	return `${value.toLocaleString('it-IT', {
		minimumFractionDigits: 1,
		maximumFractionDigits: 1,
	})} ${unit}`;
}

/** Totali e copertura di ciascun livello, più la risultante. */
function LayerTotals({ section, unit }: { section: PodDetailSection; unit: string }) {
	const { totals, coverage, realeByOrigin } = section;
	return (
		<div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
			{LAYER_KEYS.map((key) => {
				const slots = coverage[key];
				const pct = coverage.totalSlots > 0 ? Math.round((slots / coverage.totalSlots) * 100) : 0;
				return (
					<div
						key={key}
						className="rounded-md border border-border-secondary bg-bg-secondary p-2"
						// Un livello che non copre nulla resta visibile ma spento: la sua
						// assenza è informazione (il GSE non ha ancora certificato il mese).
						style={slots === 0 ? { opacity: 0.55 } : undefined}
					>
						<div className="mb-1 flex items-center gap-1.5">
							<span
								className="size-2 shrink-0 rounded-sm"
								style={{ backgroundColor: PodLayerColors[key] }}
								aria-hidden
							/>
							<span className="truncate text-[10px] text-fg-secondary">{PodLayerLabels[key]}</span>
						</div>
						<div className="font-semibold text-[13px] text-fg tabular-nums">
							{fmtKwh(totals[key], unit)}
						</div>
						<div className="text-[10px] text-fg-secondary tabular-nums">
							{pct}% · {slots}/{coverage.totalSlots} slot
						</div>
						{key === 'reale' && realeByOrigin && slots > 0 && (
							<div className="mt-0.5 text-[10px] text-fg-secondary tabular-nums">
								{Math.round((realeByOrigin.rilevato / slots) * 100)}% rilevato ·{' '}
								{Math.round((realeByOrigin.bolletta / slots) * 100)}% da bolletta
							</div>
						)}
					</div>
				);
			})}
			<div className="rounded-md border border-accent/30 bg-accent/5 p-2">
				<div className="mb-1 text-[10px] text-fg-secondary">Riconciliato</div>
				<div className="font-semibold text-[13px] text-accent tabular-nums">
					{fmtKwh(totals.riconciliato, unit)}
				</div>
				<div className="text-[10px] text-fg-secondary">usato dai KPI</div>
			</div>
		</div>
	);
}

/** Gli impianti dietro un punto di misura in immissione. */
function PlantList({ meters }: { meters: PodDetailMeter[] }) {
	const plants = meters.flatMap((m) => m.plants);
	if (plants.length === 0) return null;
	return (
		<ul className="flex flex-wrap gap-1.5">
			{plants.map((p) => (
				<li
					key={p.id}
					className="rounded-sm bg-bg-secondary px-1.5 py-0.5 text-[10px] text-fg-secondary"
				>
					<span className="font-mono">{p.censimp ?? p.codiceGaudi ?? 'senza CENSIMP'}</span>
					{p.potenzaKwp != null && <> · {p.potenzaKwp} kWp</>}
					{p.dataEsercizio && <> · in esercizio dal {p.dataEsercizio}</>}
				</li>
			))}
		</ul>
	);
}

export function PodDetailDialog({
	open,
	onOpenChange,
	codice,
	memberName,
	cabinaPrimaria,
	periodo,
	bucket,
	sections,
	unit = 'kWh',
	loading,
	pods,
	podId,
	onSelectPod,
	onBucketChange,
	buckets,
	onPrev,
	onNext,
	nextDisabled,
}: PodDetailDialogProps) {
	const shownBuckets = buckets ? BUCKETS.filter((b) => buckets.includes(b.value)) : BUCKETS;
	// Posizione nella lista: serve alle frecce e al contatore. Un POD fuori lista
	// (aperto da un'anomalia, per dire) vale come prima posizione, così le frecce
	// restano utilizzabili invece di bloccarsi.
	const podIndex = pods && podId ? pods.findIndex((p) => p.podId === podId) : -1;
	const stepPod = (delta: number) => {
		if (!pods || pods.length === 0 || !onSelectPod) return;
		const next = pods[(Math.max(podIndex, 0) + delta + pods.length) % pods.length];
		if (next) onSelectPod(next.podId);
	};
	// Il cambio ora vale un avviso: il grafico altrimenti sembra semplicemente
	// più corto o più lungo senza una ragione visibile.
	const slots = sections[0]?.coverage.totalSlots ?? 0;
	const dstNote =
		bucket === 'slot' && slots === 92
			? 'Giorno di inizio ora legale: 23 ore, 92 slot.'
			: bucket === 'slot' && slots === 100
				? 'Giorno di fine ora legale: 25 ore, 100 slot - l’ora 02:00–03:00 è ripetuta.'
				: null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-4xl">
				<DialogHeader>
					<DialogTitle className="font-mono text-base">{codice}</DialogTitle>
					<DialogDescription>
						{memberName ?? 'socio non assegnato'}
						{cabinaPrimaria ? ` · cabina ${cabinaPrimaria}` : ''} · {periodo}
					</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-4">
					{pods && pods.length > 1 && onSelectPod && (
						<div className="flex flex-wrap items-center gap-2">
							<button
								type="button"
								onClick={() => stepPod(-1)}
								aria-label="POD precedente"
								className="rounded-md border border-border-secondary px-2 py-1.5 text-fg-secondary text-xs hover:text-fg"
							>
								‹
							</button>
							<LabeledSelect
								label="POD"
								value={pods[Math.max(podIndex, 0)]?.podId ?? ''}
								onChange={onSelectPod}
							>
								{pods.map((p) => (
									<option key={p.podId} value={p.podId}>
										{p.codice}
										{p.memberName ? ` · ${p.memberName}` : ''}
									</option>
								))}
							</LabeledSelect>
							<button
								type="button"
								onClick={() => stepPod(1)}
								aria-label="POD successivo"
								className="rounded-md border border-border-secondary px-2 py-1.5 text-fg-secondary text-xs hover:text-fg"
							>
								›
							</button>
							<span className="text-[11px] text-fg-secondary">
								{Math.max(podIndex, 0) + 1} di {pods.length} · stesso intervallo e stesso grano
							</span>
						</div>
					)}

					<div className="flex flex-wrap items-center justify-between gap-2">
						<span
							className="rounded-sm bg-bg-secondary px-1.5 py-0.5 text-[10px] text-fg-secondary"
							title="Vince il livello più in alto che copre lo slot"
						>
							{LAYERS.map((l) => l.label).join(' › ')}
						</span>
						{onBucketChange && shownBuckets.length > 1 && (
							<fieldset className="flex gap-1 border-0 p-0" aria-label="Grano temporale">
								{shownBuckets.map((b) => (
									<button
										key={b.value}
										type="button"
										onClick={() => onBucketChange(b.value)}
										aria-pressed={b.value === bucket}
										className={
											'rounded-md border px-2 py-1 text-[11px] transition-colors ' +
											(b.value === bucket
												? 'border-accent bg-accent/10 text-accent'
												: 'border-border-secondary text-fg-secondary hover:text-fg')
										}
									>
										{b.label}
									</button>
								))}
							</fieldset>
						)}
					</div>

					{dstNote && (
						<p className="rounded-md bg-bg-secondary px-2.5 py-1.5 text-[11px] text-fg-secondary">
							{dstNote}
						</p>
					)}

					{loading ? (
						<div className="flex h-64 items-center justify-center text-fg-secondary text-sm">
							Caricamento dei livelli…
						</div>
					) : sections.length === 0 ? (
						<div className="flex h-64 items-center justify-center px-6 text-center text-fg-secondary text-sm">
							Nessun dato per questo POD nell'intervallo, su nessun livello e in nessuna direzione.
						</div>
					) : (
						sections.map((section) => (
							<section key={section.direction} className="flex flex-col gap-2">
								<div className="flex flex-wrap items-baseline justify-between gap-2">
									<h3 className="font-semibold text-fg text-sm">
										{DIRECTION_LABEL[section.direction]}
									</h3>
									<span className="text-[11px] text-fg-secondary">
										{DIRECTION_HINT[section.direction]}
										{section.meters.length > 1 && ` · ${section.meters.length} punti di misura`}
									</span>
								</div>

								<LayerTotals section={section} unit={unit} />
								{section.direction === 'produzione' && <PlantList meters={section.meters} />}

								{section.points.length === 0 ? (
									<div className="flex h-40 items-center justify-center text-fg-secondary text-sm">
										Nessuna curva per questa direzione.
									</div>
								) : (
									<PodLayersChart data={section.points} unit={unit} height={240} />
								)}
							</section>
						))
					)}

					{(onPrev || onNext) && (
						<div className="flex items-center justify-between">
							<button
								type="button"
								onClick={onPrev}
								disabled={!onPrev}
								className="rounded-md border border-border-secondary px-3 py-1.5 text-fg-secondary text-xs hover:text-fg disabled:opacity-40"
							>
								← Intervallo precedente
							</button>
							<button
								type="button"
								onClick={onNext}
								disabled={!onNext || nextDisabled}
								className="rounded-md border border-border-secondary px-3 py-1.5 text-fg-secondary text-xs hover:text-fg disabled:opacity-40"
							>
								Intervallo successivo →
							</button>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
